import type { Theme } from '@mui/material/styles';
import type { ApexOptions } from 'apexcharts';
import { statusColor } from '@/components/misc/status-color';
import { syntheticUtcMsToDayKey } from '@/utils/orgCalendar';

export type DailyValueFormat = 'currency' | 'count';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const currencyTooltipFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const countFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatChartValue = (value: number, format: DailyValueFormat): string => {
  const safeValue = Number.isFinite(value) ? value : 0;
  return format === 'currency' ? currencyFormatter.format(safeValue) : countFormatter.format(safeValue);
};

export const formatChartTooltipValue = (value: number | null | undefined, format: DailyValueFormat): string => {
  if (value == null) return '—';
  const safeValue = Number.isFinite(value) ? value : 0;
  return format === 'currency' ? currencyTooltipFormatter.format(safeValue) : countFormatter.format(safeValue);
};

export const statusSeriesColors = (theme: Theme, names: readonly string[]): string[] =>
  names.map((name) => {
    const color = statusColor(name);
    if (color === 'default') return theme.palette.mode === 'light' ? theme.palette.neutral[200] : theme.palette.neutral[800];
    return theme.palette[color].main;
  });

type DailyChartOptionsArgs = {
  format: DailyValueFormat;
  stacked?: boolean;
  colors?: string[];
  barTotals?: boolean;
};

export const dailyChartOptions = (theme: Theme, { format, stacked = false, colors, barTotals = false }: DailyChartOptionsArgs): ApexOptions => ({
  chart: {
    background: 'transparent',
    foreColor: theme.palette.text.primary,
    stacked,
    animations: { enabled: false },
    toolbar: {
      show: true,
      export: {
        csv: {
          categoryFormatter: (value?: number): string => (typeof value === 'number' && Number.isFinite(value) ? syntheticUtcMsToDayKey(value) : ''),
        },
      },
    },
    zoom: { enabled: false },
  },
  ...(colors ? { colors } : {}),
  dataLabels: { enabled: false },
  fill: { opacity: 1 },
  grid: {
    borderColor: theme.palette.divider,
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: true } },
  },
  legend: {
    show: true,
    showForSingleSeries: true,
    showForZeroSeries: false,
  },
  ...(barTotals
    ? {
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 10,
            dataLabels: {
              total: {
                enabled: true,
                formatter: (value: string): string => formatChartValue(Number(value), 'count'),
                style: { fontSize: '13px', fontWeight: 900 },
              },
            },
          },
        },
      }
    : {}),
  stroke: { curve: 'smooth', show: true },
  theme: { mode: theme.palette.mode },
  xaxis: {
    type: 'datetime',
    labels: { datetimeUTC: true },
  },
  yaxis: {
    min: 0,
    labels: {
      formatter: (value: number): string => formatChartValue(value, format),
      offsetX: -12,
      style: { colors: [theme.palette.text.secondary] },
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    x: { format: 'MM/dd/yy' },
    y: { formatter: (value: number): string => formatChartTooltipValue(value, format) },
  },
});
